import { useMemo, useState } from "react";
import { Input, message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  useOrganizationsList,
  useUpdateOrganizationStatus,
} from "../../../api/organization";
import { AppButton } from "../../ui/AppButton";
import { Organization } from "../../../types/organization";
import { Button } from "../../ui/button";
import StatusUpdate from "../../modals/StatusUpdate";
import { Icons } from "../../../assets/images/icons";
import { StatusBadge } from "../../shared/status-badge";
import { DataTable } from "../../ui/data-table";
import { useTablePrint } from "../../../hooks/useTablePrint";
import { ExportButton } from "../../ui/ExportButton";

const OrganizationTable = () => {
  const navigate = useNavigate();

  /** State management */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: organizations, isLoading } = useOrganizationsList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  const { mutateAsync: updateStatus, isPending } =
    useUpdateOrganizationStatus();

  /** Transform organizations for table */
  const transformedOrganizations = useMemo((): Organization[] => {
    if (!organizations?.data?.content) return [];
    return organizations.data.content.map((item: Organization) => ({
      id: item.id,
      name: item.name || "N/A",
      type: item.type || "N/A",
      email: item.email,
      phoneNo: item.phoneNo || "N/A",
      status: item.status, // for display only
    }));
  }, [organizations]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleStatusUpdate = async (status: string) => {
    try {
      if (selectedId && status) {
        await updateStatus({ id: selectedId, status });
        message.success("Organization status updated successfully.");
      }
    } catch (err) {
      message.error("Operation failed");
    } finally {
      setOpen(false);
    }
  };

  const handleUpdateStatus = (org: Organization) => {
    setSelectedId(org.id);
    setSelectedOrg(org);
    setOpen(true);
  };

  const handleRowClick = (org: Organization) => {
    navigate(`/organizations/${org.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Columns */
  const organizationColumns = [
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      align: "left" as const,
    },
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      align: "left" as const,
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      align: "left" as const,
    },
    {
      key: "phoneNo",
      title: "Phone",
      dataIndex: "phoneNo",
      align: "left" as const,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: string) => <StatusBadge value={value} />,
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (_: any, record: Organization) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleUpdateStatus(record);
          }}
          // disabled={record.status.toLowerCase() !== "pending"}
          className="h-8 p-0 w-full gap-2"
        >
          <img src={Icons.pencilEdit} alt="edit" className="h-6 w-6" /> Update
          Status
        </Button>
      ),
    },
  ];

  return (
    <>
      <div
        className="bg-transparent lg:bg-white p-4 lg:p-6"
        style={{
          borderRadius: 8.5,
          marginTop: 25,
        }}
      >
        <div className="flex flex-col sm:flex-row justify-between">
          {/** Search - Desktop */}
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search merchants by name"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(
                  organizationColumns,
                  transformedOrganizations,
                  "Organization Report",
                )
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={organizationColumns}
              data={transformedOrganizations}
              fileName="organizations"
              title="Organization Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: organizations?.data?.totalElements ?? 0,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />
          </div>
        </div>

        {/** Desktop Table View */}
        <div className="hidden md:block">
          <DataTable<Organization>
            data={transformedOrganizations}
            columns={organizationColumns}
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: organizations?.data?.totalElements ?? 0,
              onChange: handlePaginationChange,
            }}
            onRowClick={handleRowClick}
            emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
            emptyText="No organizations found"
            rowKey="id"
          />
        </div>
      </div>

      <div className="hidden md:flex flex-col gap-2">
        {/** Status update modal */}
        <StatusUpdate
          open={open}
          onClose={() => setOpen(false)}
          onUpdate={handleStatusUpdate}
          isPending={isPending}
          currentStatus={selectedOrg?.status}
        />
      </div>
    </>
  );
};

export default OrganizationTable;
